import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { userService } from "../services/userService";
import { formatCurrency, formatDate, getStatusBadgeVariant } from "../utils/userDataFormatter";

import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Table,
  ProgressBar,
  Alert,
  Spinner,
  Form,
  Image,
  Modal,
} from "react-bootstrap";
import {
  FaUser,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaHistory,
  FaCreditCard,
  FaBell,
  FaCheckCircle,
  FaChartLine,
  FaEye,
} from "react-icons/fa";

const UserInfo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [spendingFilter, setSpendingFilter] = useState("month");
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [weeklySpending, setWeeklySpending] = useState([]);
  const [monthlySpending, setMonthlySpending] = useState([]); // Add monthly spending state
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    fetchUserData();
    fetchUserBookings();
    fetchUserSubscription();
    fetchAnalytics();
  }, [id]);

  const parseDateValue = (value) => {
    try {
      if (!value) return null;
      if (typeof value === "string" || typeof value === "number") {
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d.toISOString();
      }
      // Firestore Timestamp { seconds, nanoseconds }
      if (typeof value === "object" && (value.seconds || value._seconds)) {
        const secs = value.seconds || value._seconds;
        return new Date(secs * 1000).toISOString();
      }
      // Nested date object
      if (value?.toDate) {
        const d = value.toDate();
        return d instanceof Date && !isNaN(d) ? d.toISOString() : null;
      }
      return null;
    } catch (_) {
      return null;
    }
  };

  // Add a more specific function for date of birth parsing
  const parseDateOfBirth = (value) => {
    try {
      if (!value) return "—";

      // Handle Firestore Timestamp objects
      if (typeof value === "object" && (value.seconds || value._seconds)) {
        const secs = value.seconds || value._seconds;
        const date = new Date(secs * 1000);
        return isNaN(date.getTime()) ? "—" : date.toISOString();
      }

      // Handle string dates
      if (typeof value === "string") {
        const date = new Date(value);
        return isNaN(date.getTime()) ? "—" : date.toISOString();
      }

      // Handle number timestamps
      if (typeof value === "number") {
        const date = new Date(value);
        return isNaN(date.getTime()) ? "—" : date.toISOString();
      }

      // Handle Date objects
      if (value instanceof Date) {
        return isNaN(value.getTime()) ? "—" : value.toISOString();
      }

      return "—";
    } catch (error) {
      console.error("Error parsing date of birth:", error);
      return "—";
    }
  };

  const deriveBilling = (plan, providedType) => {
    const type = (providedType || "").toString().toLowerCase();
    if (type && type !== "—") return providedType;
    const p = (plan || "").toString().toLowerCase();
    if (p === "basic") return "monthly";
    if (p === "standard") return "quarterly";
    if (p === "premium") return "yearly";
    if (p === "none") return "30 days";
    return providedType || "—";
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const data = await userService.getUserById(id);
      const u = data.user || {};
      // Normalize subscription similar to vendor subscription structure
      const subRaw = u.subscription;
      const normalizedSubscription = subRaw
        ? typeof subRaw === "string"
          ? {
              plan: subRaw,
              type: deriveBilling(subRaw, "—"),
              status: "active",
              startDate: parseDateValue(
                u.subscriptionStart ||
                  u.start ||
                  u.startDate ||
                  u.start_date ||
                  u.started_at
              ),
              endDate: parseDateValue(
                u.subscriptionEnd ||
                  u.end ||
                  u.endDate ||
                  u.end_date ||
                  u.ends_at
              ),
            }
          : {
              plan:
                subRaw.plan ||
                subRaw.name ||
                subRaw.tier ||
                subRaw.level ||
                subRaw.package ||
                "—",
              type: deriveBilling(
                subRaw.plan ||
                  subRaw.name ||
                  subRaw.tier ||
                  subRaw.level ||
                  subRaw.package,
                subRaw.type ||
                  subRaw.billing ||
                  subRaw.interval ||
                  subRaw.duration ||
                  subRaw.cadence ||
                  "—"
              ),
              status: subRaw.status || "active",
              startDate: parseDateValue(
                subRaw.startDate ||
                  subRaw.start_date ||
                  subRaw.started_at ||
                  subRaw.start ||
                  u.subscriptionStart ||
                  u.start
              ),
              endDate: parseDateValue(
                subRaw.endDate ||
                  subRaw.end_date ||
                  subRaw.ends_at ||
                  subRaw.end ||
                  u.subscriptionEnd ||
                  u.end
              ),
            }
        : null;

      // If subscription plan is 'none', ensure a 30-day window by default
      const sub = normalizedSubscription;
      let finalizedSubscription = sub;
      try {
        const nowIso = new Date().toISOString();
        if (!sub) {
          const start = new Date(nowIso);
          const end = new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000);
          finalizedSubscription = {
            plan: "none",
            type: "—",
            status: "active",
            startDate: start.toISOString(),
            endDate: end.toISOString(),
          };
        } else if (String(sub.plan || "").toLowerCase() === "none") {
          const startIso = sub.startDate || nowIso;
          const start = new Date(startIso);
          if (!isNaN(start)) {
            const end = new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000);
            finalizedSubscription = {
              ...sub,
              startDate: start.toISOString(),
              endDate: sub.endDate || end.toISOString(),
            };
          }
        }
      } catch (_) {}

      const mapped = {
        id: u.id || u._id || id,
        name: u.name || "—",
        email: u.email || "—",
        phone: u.phone || "—",
        address: u.address || "—",
        pincode: u.pincode || "—",
        societyName: u.societyName || "—", // Add society name
        gender: u.gender || "—", // Add gender
        dateOfBirth: parseDateOfBirth(u.dateOfBirth), // Use the specific function for date of birth
        profileImage: u.profileImage || null,
        isProfileComplete: !!u.isProfileComplete,
        status: u.isActive === false ? "inactive" : "active",
        isActive: u.isActive !== false,
        subscription: finalizedSubscription,
        memberSince: u.createdAt || new Date().toISOString(),
        createdAt: u.createdAt || null,
        updatedAt: u.updatedAt || null,
        totalBookings: u.totalBookings || 0,
        totalSpent: u.totalSpent || 0,
        averageRating: u.averageRating || 0,
        referralsCount: u.referralsCount || 0,
        referralCode: u.referralCode || "—", // Add referral code
        referralPoints: u.referralPoints || 0, // Add referral points
        // Remove emergency contact fields as requested
        preferences: {
          notifications: true,
          emailUpdates: true,
          smsUpdates: false,
        },
        paymentMethods: [],
      };
      setUserData(mapped);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setError("Failed to load user data");
      setLoading(false);
    }
  };

  const fetchUserBookings = async () => {
    try {
      const bookingsArr = await userService.getUserBookings(id);
      const mapped = (bookingsArr || []).map((b) => ({
        id: b.id,
        vendorName: b.vendorName || b.vendorId || "—",
        vendorRating: b.vendorRating || 0,
        vendorImage: b.vendorImage || "",
        vendorAddress: b.vendorAddress || "",
        vendorPhone: b.vendorPhone || "",
        serviceName: b.serviceName || "—",
        dateTime: b.dateTime || b.createdAt || new Date().toISOString(),
        status: b.status || "pending",
        price: typeof b.price === "number" ? b.price : parseFloat(b.price || 0),
        rating: b.rating || null,
        customerName: b.customerName || "",
        customerPhone: b.customerPhone || "",
        customerAddress: b.customerAddress || b.address || "",
        customerImage: b.customerImage || "",
        description: b.description || "",
        createdAt: b.createdAt,
        updatedAt: b.updatedAt,
      }));
      setBookings(mapped);
      // Update totals based on bookings fetched
      const totalSpent = mapped.reduce(
        (sum, bk) => sum + (Number.isFinite(bk.price) ? bk.price : 0),
        0
      );
      const totalBookings = mapped.length;
      setUserData((prev) =>
        prev ? { ...prev, totalBookings, totalSpent } : prev
      );

      // Compute repeat services grouping by serviceName
      const svcMap = {};
      const svcToBookings = {};
      for (const bk of mapped) {
        const key = (bk.serviceName || "—").toString();
        if (!svcMap[key]) {
          svcMap[key] = {
            serviceName: key,
            count: 0,
            totalSpent: 0,
            lastAt: null,
          };
          svcToBookings[key] = [];
        }
        svcMap[key].count += 1;
        svcMap[key].totalSpent += Number(bk.price || 0);
        const when = new Date(bk.dateTime || bk.createdAt || Date.now());
        if (!svcMap[key].lastAt || when > svcMap[key].lastAt)
          svcMap[key].lastAt = when;
        svcToBookings[key].push(bk);
      }
      const repeats = Object.values(svcMap)
        .filter((s) => s.count > 1)
        .sort((a, b) => b.count - a.count || b.totalSpent - a.totalSpent)
        .slice(0, 20);
    } catch (e) {
      console.error("Error fetching user bookings:", e);
      // Fallback to mock data if real data fails
      console.log("Falling back to mock booking data");
      setBookings(generateMockBookings(10));
    }
  };

  const fetchUserSubscription = async () => {
    try {
      // Use dynamic base URL approach like other admin components
      const candidateBases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean)
        )
      );

      let data = null;
      for (const base of candidateBases) {
        try {
          // Get admin token for authentication
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {};
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          const res = await fetch(`${base}/api/users/${id}/subscription`, {
            headers,
          });
          if (!res.ok) continue;
          const ct = res.headers.get("content-type") || "";
          if (!ct.includes("application/json")) continue;
          data = await res.json();
          break;
        } catch (_) {
          /* try next */
        }
      }
      const nowIso = new Date().toISOString();
      if (!data || data.subscription == null) {
        // No subscription returned: default to 'none' with 30-day window
        const start = new Date(nowIso);
        const end = new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000);
        setUserData((prev) =>
          prev
            ? {
                ...prev,
                subscription: {
                  plan: "none",
                  type: "—",
                  status: "active",
                  startDate: start.toISOString(),
                  endDate: end.toISOString(),
                },
              }
            : prev
        );
        return;
      }
      const subRaw = data.subscription;
      let normalized = subRaw
        ? {
            plan:
              subRaw.plan ||
              subRaw.name ||
              subRaw.tier ||
              subRaw.level ||
              subRaw.package ||
              "—",
            type: deriveBilling(
              subRaw.plan ||
                subRaw.name ||
                subRaw.tier ||
                subRaw.level ||
                subRaw.package,
              subRaw.type ||
                subRaw.billing ||
                subRaw.interval ||
                subRaw.duration ||
                subRaw.cadence ||
                "—"
            ),
            status: subRaw.status || "active",
            startDate: parseDateValue(
              subRaw.startDate ||
                subRaw.start_date ||
                subRaw.started_at ||
                subRaw.start ||
                subRaw.begin ||
                subRaw.active_from ||
                subRaw.valid_from
            ),
            endDate: parseDateValue(
              subRaw.endDate ||
                subRaw.end_date ||
                subRaw.ends_at ||
                subRaw.end ||
                subRaw.expires_at ||
                subRaw.valid_to
            ),
          }
        : null;
      // Ensure 30-day window for plan 'none', and minimums for quarterly/yearly
      try {
        if (!normalized) {
          const start = new Date(nowIso);
          const end = new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000);
          normalized = {
            plan: "none",
            type: "—",
            status: "active",
            startDate: start.toISOString(),
            endDate: end.toISOString(),
          };
        } else if (String(normalized.plan || "").toLowerCase() === "none") {
          const startIso = normalized.startDate || nowIso;
          const start = new Date(startIso);
          if (!isNaN(start)) {
            const end = new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000);
            normalized = {
              ...normalized,
              startDate: start.toISOString(),
              endDate: normalized.endDate || end.toISOString(),
            };
          }
        } else {
          // quarterly/yearly enforcement
          const typeLc = String(normalized.type || "").toLowerCase();
          const startIso = normalized.startDate || nowIso;
          const start = new Date(startIso);
          if (!isNaN(start)) {
            const addMonths = (date, months) => {
              const d = new Date(
                Date.UTC(
                  date.getUTCFullYear(),
                  date.getUTCMonth(),
                  date.getUTCDate(),
                  date.getUTCHours(),
                  date.getUTCMinutes(),
                  date.getUTCSeconds(),
                  date.getUTCMilliseconds()
                )
              );
              const y = d.getUTCFullYear();
              const m = d.getUTCMonth();
              const day = d.getUTCDate();
              const h = d.getUTCHours();
              const mi = d.getUTCMinutes();
              const s = d.getUTCSeconds();
              const ms = d.getUTCMilliseconds();
              const target = new Date(Date.UTC(y, m + months, 1, h, mi, s, ms));
              const daysInTargetMonth = new Date(
                Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)
              ).getUTCDate();
              target.setUTCDate(Math.min(day, daysInTargetMonth));
              return target;
            };
            if (typeLc === "quarterly") {
              const minEnd = addMonths(start, 3);
              if (
                !normalized.endDate ||
                new Date(normalized.endDate) < minEnd
              ) {
                normalized = { ...normalized, endDate: minEnd.toISOString() };
              }
            } else if (
              typeLc === "yearly" ||
              typeLc === "annual" ||
              typeLc === "annually"
            ) {
              const minEnd = addMonths(start, 12);
              if (
                !normalized.endDate ||
                new Date(normalized.endDate) < minEnd
              ) {
                normalized = { ...normalized, endDate: minEnd.toISOString() };
              }
            }
          }
        }
      } catch (_) {}
      setUserData((prev) =>
        prev ? { ...prev, subscription: normalized } : prev
      );
    } catch (e) {
      // ignore subscription fetch errors to avoid breaking page
    }
  };

  // Function to fetch real-time analytics data
  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      console.log(`📊 Fetching analytics for user: ${id}`);

      // Use dynamic base URL approach like other admin components
      const candidateBases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean)
        )
      );

      let analyticsData = null;
      let weeklySpendingData = [];
      let monthlySpendingData = []; // Add monthly spending data variable
      let lastError = null;

      // Get admin token for authentication
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");
      const headers = {};
      if (adminToken) {
        headers["Authorization"] = `Bearer ${adminToken}`;
      }

      for (const base of candidateBases) {
        try {
          console.log(`🔍 Trying analytics API with base: ${base}`);

          // Fetch statistics
          const statsResponse = await fetch(
            `${base}/api/users/${id}/statistics`,
            { headers }
          );
          if (statsResponse.ok) {
            const statsData = await statsResponse.json();
            analyticsData = statsData;
            console.log(`✅ Analytics fetched successfully from: ${base}`);

            // Fetch weekly spending
            const weeklyResponse = await fetch(
              `${base}/api/users/${id}/weekly-spending`,
              { headers }
            );
            if (weeklyResponse.ok) {
              const weeklyData = await weeklyResponse.json();
              weeklySpendingData = weeklyData.weeklySpending || [];
              console.log(
                `✅ Weekly spending fetched successfully from: ${base}`
              );
            }

            // Fetch monthly spending
            const monthlyResponse = await fetch(
              `${base}/api/users/${id}/monthly-spending`,
              { headers }
            );
            if (monthlyResponse.ok) {
              const monthlyData = await monthlyResponse.json();
              monthlySpendingData = monthlyData.monthlySpending || [];
              console.log(
                `✅ Monthly spending fetched successfully from: ${base}`
              );
            }
            break;
          } else {
            console.log(
              `❌ Analytics API failed with status: ${statsResponse.status}`
            );
          }
        } catch (error) {
          console.log(
            `❌ Analytics API error with base ${base}:`,
            error.message
          );
          lastError = error;
        }
      }

      if (analyticsData) {
        setAnalytics(analyticsData);
        setWeeklySpending(weeklySpendingData);
        setMonthlySpending(monthlySpendingData); // Set monthly spending data
        console.log(`📊 Analytics data loaded:`, analyticsData);
      } else {
        console.warn("⚠️ Could not fetch analytics data, using fallback");
        // Set fallback data
        setAnalytics({
          totalBookings: 0,
          totalSpent: 0,
          averageRating: 0,
          completedBookings: 0,
          pendingBookings: 0,
          cancelledBookings: 0,
          thisMonthSpent: 0,
          thisMonthBookings: 0,
          lastMonthSpent: 0,
          lastMonthBookings: 0,
          thisWeekSpent: 0,
          thisWeekBookings: 0,
          thisYearSpent: 0,
          thisYearBookings: 0,
          completionRate: 0,
          spending: { week: 0, month: 0, year: 0, allTime: 0 },
        });
        setWeeklySpending([]);
        setMonthlySpending([]); // Set empty monthly spending data
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
      setError("Failed to load analytics data");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Generate mock bookings for the user
  const generateMockBookings = (count) => {
    const statuses = ["completed", "in-progress", "pending", "cancelled"];
    const services = [
      "Home Cleaning",
      "AC Service",
      "Plumbing",
      "Electrician",
      "Pest Control",
    ];
    const vendors = [
      { name: "SparkClean Co.", rating: 4.6 },
      { name: "CoolBreeze HVAC", rating: 4.3 },
      { name: "RapidPlumb", rating: 4.1 },
      { name: "VoltFix Electric", rating: 4.5 },
      { name: "BugAway Pros", rating: 4.2 },
    ];
    return Array.from({ length: count }, (_, i) => {
      const status = statuses[Math.floor(Math.random() * statuses.length)];
      const serviceName = services[Math.floor(Math.random() * services.length)];
      const vendor = vendors[Math.floor(Math.random() * vendors.length)];
      const daysAgo = Math.floor(Math.random() * 180);
      const dateTime = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000);
      const price = [49, 79, 99, 149, 199][Math.floor(Math.random() * 5)];
      const rating =
        status === "completed" ? (Math.random() * 2 + 3).toFixed(1) : null;
      return {
        id: `UB${1000 + i}`,
        vendorName: vendor.name,
        vendorRating: vendor.rating,
        serviceName,
        dateTime,
        status,
        price,
        rating,
      };
    });
  };

  const openBookingModal = (booking) => {
    setSelectedBooking(booking);
    setShowBookingModal(true);
  };

  const closeBookingModal = () => {
    setShowBookingModal(false);
    setSelectedBooking(null);
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      // Use dynamic base URL approach like other admin components
      const bases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean)
        )
      );

      let success = false;
      let lastError = null;

      // Get admin token for authentication
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");
      const headers = {
        "Content-Type": "application/json",
      };
      if (adminToken) {
        headers["Authorization"] = `Bearer ${adminToken}`;
      }

      // Try to cancel booking from each base URL until one works
      for (const base of bases) {
        try {
          const res = await fetch(`${base}/api/bookings/${bookingId}/cancel`, {
            method: "PUT",
            headers,
          });

          if (!res.ok) {
            throw new Error(
              `Failed to cancel booking: ${res.status} ${res.statusText}`
            );
          }

          success = true;
          break;
        } catch (e) {
          lastError = e;
          continue;
        }
      }

      if (!success)
        throw (
          lastError || new Error("Failed to cancel booking from any endpoint")
        );

      // Update the bookings state to reflect the cancellation
      setBookings((prev) =>
        prev.map((booking) =>
          booking.id === bookingId
            ? { ...booking, status: "cancelled" }
            : booking
        )
      );

      // Also update the userData if needed
      setUserData((prev) => ({
        ...prev,
        totalBookings: prev.totalBookings > 0 ? prev.totalBookings - 1 : 0,
      }));
    } catch (error) {
      console.error("Error cancelling booking:", error);
      // You might want to show an error message to the user here
      alert("Failed to cancel booking: " + error.message);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: "success",
      "in-progress": "warning",
      pending: "info",
      cancelled: "danger",
    };
    return <Badge bg={variants[status] || "secondary"}>{status}</Badge>;
  };

  const getSubscriptionBadge = (planName) => {
    const variants = {
      premium: "success",
      basic: "primary",
      standard: "info",
      none: "secondary",
    };
    const key = String(planName || "none").toLowerCase();
    return (
      <Badge bg={variants[key] || "secondary"}>{planName || "none"}</Badge>
    );
  };

  // Function to get weekly spending data (last 7 days)
  const getWeeklySpendingData = () => {
    if (weeklySpending && weeklySpending.length > 0) {
      return weeklySpending;
    }

    // Fallback to empty data if no real data available
    const today = new Date();
    const fallbackData = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dayName = date.toLocaleDateString("en-US", { weekday: "short" });
      const dayNumber = date.getDate();

      fallbackData.push({
        day: `${dayName} ${dayNumber}`,
        fullDate: date.toISOString().split("T")[0],
        spent: 0,
        bookings: 0,
        hasSpending: false,
      });
    }
    return fallbackData;
  };

  const getSpendingByMonth = () => {
    // Use real monthly spending data if available, otherwise fallback to mock data
    if (monthlySpending && monthlySpending.length > 0) {
      return monthlySpending;
    }

    // Mock monthly spending data
    return [
      { month: "Jan", spent: 800 },
      { month: "Feb", spent: 1200 },
      { month: "Mar", spent: 600 },
      { month: "Apr", spent: 900 },
      { month: "May", spent: 1100 },
      { month: "Jun", spent: 750 },
      { month: "Jul", spent: 980 },
      { month: "Aug", spent: 1050 },
      { month: "Sep", spent: 870 },
      { month: "Oct", spent: 940 },
      { month: "Nov", spent: 1120 },
      { month: "Dec", spent: 1250 },
    ];
  };

  const getSpendingTotals = () => {
    // Mock totals for filters
    return {
      week: 320,
      month: 2750,
      year: 3200,
      allTime: 3200,
    };
  };

  const getFilteredSpending = () => {
    if (!analytics || !analytics.spending) return 0;

    return analytics.spending[spendingFilter] || 0;
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
        <Button onClick={fetchUserData} variant="outline-danger">
          Retry
        </Button>
      </Alert>
    );
  }

  if (!userData) {
    return <div>No user data available</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <div className="me-3">
            {userData.profileImage ? (
              <Image
                src={userData.profileImage}
                roundedCircle
                width={60}
                height={60}
                alt={userData.name}
              />
            ) : (
              <div
                className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: 60, height: 60 }}
              >
                <FaUser size={30} />
              </div>
            )}
          </div>
          <div>
            <h1 className="mb-1">{userData.name}!</h1>
            <p className="text-muted mb-0">
              Member since {new Date(userData.memberSince).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards - REMOVED AS PER REQUEST */}
      {/* <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <FaCalendar className="text-primary mb-2" size={30} />
              <Card.Title>{userData.totalBookings}</Card.Title>
              <Card.Text>Total Bookings</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <div className="text-success mb-2" style={{ fontSize: '20px' }}>₹</div>
              <Card.Title>{userData.totalSpent.toLocaleString()}</Card.Title>
              <Card.Text>Total Spent</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <FaHeart className="text-danger mb-2" size={30} />
              <Card.Title>{userData.referralsCount ?? 0}</Card.Title>
              <Card.Text>Refer Friends</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row> */}

      {/* Main Content Tabs */}
      <div className="d-flex flex-wrap gap-2 mb-4">
        <Button
          variant={activeTab === "overview" ? "primary" : "outline-primary"}
          onClick={() => setActiveTab("overview")}
          className="d-flex align-items-center"
        >
          <FaUser className="me-2" />
          Overview
        </Button>
        <Button
          variant={activeTab === "bookings" ? "primary" : "outline-primary"}
          onClick={() => setActiveTab("bookings")}
          className="d-flex align-items-center"
        >
          <FaHistory className="me-2" />
          Bookings
        </Button>
        <Button
          variant={activeTab === "payments" ? "primary" : "outline-primary"}
          onClick={() => setActiveTab("payments")}
          className="d-flex align-items-center"
        >
          <FaCreditCard className="me-2" />
          Subscription
        </Button>
        <Button
          variant={activeTab === "analytics" ? "primary" : "outline-primary"}
          onClick={() => setActiveTab("analytics")}
          className="d-flex align-items-center"
        >
          <FaChartLine className="me-2" />
          Analytics
        </Button>
      </div>

      {/* Tab Content */}
      <div>
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <Row>
            <Col md={8}>
              {/* Profile Information */}
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">
                    <FaUser className="me-2" />
                    Personal Profile
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <h6>Personal Information</h6>
                      <p>
                        <strong>Name:</strong> {userData.name}
                      </p>
                      <p>
                        <strong>Email:</strong> {userData.email}
                      </p>
                      <p>
                        <strong>Phone:</strong> {userData.phone}
                      </p>
                      <p>
                        <strong>Gender:</strong> {userData.gender}
                      </p>
                      <p>
                        <strong>Date of Birth:</strong>{" "}
                        {userData.dateOfBirth !== "—"
                          ? new Date(userData.dateOfBirth).toLocaleDateString()
                          : "—"}
                      </p>
                      <p>
                        <strong>Status:</strong>{" "}
                        {getStatusBadge(userData.status)}
                      </p>
                      <p>
                        <strong>Profile Complete:</strong>{" "}
                        {userData.isProfileComplete ? "Yes" : "No"}
                      </p>
                      <p>
                        <strong>Subscription:</strong>{" "}
                        {getSubscriptionBadge(userData.subscription?.plan)}
                      </p>
                    </Col>
                    <Col md={6}>
                      <h6>Address</h6>
                      <p>
                        <FaMapMarkerAlt className="me-2" />
                        {userData.address}
                      </p>
                      <p>
                        <strong>Pincode:</strong> {userData.pincode}
                      </p>
                      <p>
                        <strong>Society Name:</strong> {userData.societyName}
                      </p>

                      <h6 className="mt-3">Referral Program</h6>
                      <p>
                        <strong>Referral Code:</strong> {userData.referralCode}
                      </p>
                      <p>
                        <strong>Total Points:</strong> {userData.referralPoints}
                      </p>
                      <p>
                        <strong>Refer Friends Count:</strong>{" "}
                        {userData.referralsCount}
                      </p>

                      <h6 className="mt-3">Preferences</h6>
                      <p>
                        <FaBell className="me-2" />
                        Notifications:{" "}
                        {userData.preferences.notifications ? "On" : "Off"}
                      </p>
                      <p>
                        <FaEnvelope className="me-2" />
                        Email Updates:{" "}
                        {userData.preferences.emailUpdates ? "On" : "Off"}
                      </p>
                      <p>
                        <FaPhone className="me-2" />
                        SMS Updates:{" "}
                        {userData.preferences.smsUpdates ? "On" : "Off"}
                      </p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Recent Bookings */}
              <Card>
                <Card.Header>
                  <h5 className="mb-0">
                    <FaHistory className="me-2" />
                    Recent Bookings
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Vendor</th>
                        <th>Service</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.slice(0, 5).map((booking) => (
                        <tr key={booking.id}>
                          <td>
                            <div>
                              <strong>{booking.vendorName}</strong>
                            </div>
                          </td>
                          <td>{booking.serviceName}</td>
                          <td>
                            {new Date(booking.dateTime).toLocaleDateString()}
                          </td>
                          <td>{getStatusBadge(booking.status)}</td>
                          <td>₹{booking.price}</td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => openBookingModal(booking)}
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              {/* Quick Actions */}
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">Quick Actions</h5>
                </Card.Header>
                <Card.Body>
                  <div className="d-grid gap-2">
                    <Button
                      variant="outline-primary"
                      onClick={() => setActiveTab("analytics")}
                    >
                      <FaChartLine className="me-2" />
                      View Analytics
                    </Button>
                    <Button
                      variant="outline-success"
                      onClick={() => navigate(`/user-profile/${id}`)}
                    >
                      <FaEye className="me-2" />
                      View Profile
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              {/* Account Status */}
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">Account Status</h5>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between">
                      <span>Profile Completion</span>
                      <span>{userData.isProfileComplete ? "100%" : "75%"}</span>
                    </div>
                    <ProgressBar
                      now={userData.isProfileComplete ? 100 : 75}
                      className="mt-1"
                      variant={
                        userData.isProfileComplete ? "success" : "warning"
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between">
                      <span>Subscription</span>
                      <span>
                        {getSubscriptionBadge(userData.subscription?.plan)}
                      </span>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between">
                      <span>Verification</span>
                      <span>
                        <FaCheckCircle className="text-success" />
                        Verified
                      </span>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Monthly Spending Chart */}
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Monthly Spending</h5>
                </Card.Header>
                <Card.Body>
                  {getSpendingByMonth().map((item, index) => (
                    <div
                      key={index}
                      className="d-flex justify-content-between align-items-center mb-2"
                    >
                      <span>{item.month}</span>
                      <span className="fw-bold">
                        ₹{item.spent.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <Card>
            <Card.Header>
              <h5 className="mb-0">All Bookings</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Vendor</th>
                    <th>Service</th>
                    <th>Date & Time</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking.id}>
                      <td>{booking.id}</td>
                      <td>
                        <div>
                          <strong>{booking.vendorName}</strong>
                        </div>
                      </td>
                      <td>{booking.serviceName}</td>
                      <td>{new Date(booking.dateTime).toLocaleString()}</td>
                      <td>{getStatusBadge(booking.status)}</td>
                      <td>₹{booking.price}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                          onClick={() => openBookingModal(booking)}
                        >
                          View
                        </Button>
                        {booking.status === "completed" && !booking.rating && (
                          <Button
                            variant="outline-warning"
                            size="sm"
                            className="me-1"
                          >
                            Rate
                          </Button>
                        )}
                        {booking.status === "pending" && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleCancelBooking(booking.id)}
                          >
                            Cancel
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {/* Subscription Tab */}
        {activeTab === "payments" && (
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Subscription</h5>
            </Card.Header>
            <Card.Body>
              {userData.subscription ? (
                <>
                  <Row className="mb-3">
                    <Col md={6}>
                      <div className="mb-2">
                        <Badge
                          bg={
                            (
                              userData.subscription.status || ""
                            ).toLowerCase() === "active"
                              ? "success"
                              : (
                                  userData.subscription.status || ""
                                ).toLowerCase() === "expired"
                              ? "secondary"
                              : (
                                  userData.subscription.status || ""
                                ).toLowerCase() === "cancelled"
                              ? "danger"
                              : (
                                  userData.subscription.status || ""
                                ).toLowerCase() === "pending"
                              ? "warning"
                              : "light"
                          }
                        >
                          {(
                            userData.subscription.status || "UNKNOWN"
                          ).toUpperCase()}
                        </Badge>
                      </div>
                      <p className="mb-1">
                        <strong>Plan:</strong>{" "}
                        {userData.subscription.plan || "—"}
                      </p>
                      <p className="mb-1">
                        <strong>Billing:</strong>{" "}
                        {userData.subscription.type || "—"}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-1">
                        <strong>Start:</strong>{" "}
                        {userData.subscription.startDate
                          ? new Date(
                              userData.subscription.startDate
                            ).toLocaleDateString()
                          : "—"}
                      </p>
                      <p className="mb-0">
                        <strong>End:</strong>{" "}
                        {userData.subscription.endDate
                          ? new Date(
                              userData.subscription.endDate
                            ).toLocaleDateString()
                          : "—"}
                      </p>
                    </Col>
                  </Row>
                </>
              ) : (
                <Alert variant="light" className="mb-3">
                  No active subscription for this user.
                </Alert>
              )}
              {userData.paymentMethods.length > 0 && (
                <Row>
                  {userData.paymentMethods.map((method) => (
                    <Col md={6} key={method.id} className="mb-3">
                      <Card>
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6>
                                {method.brand} •••• {method.last4}
                              </h6>
                              <p className="text-muted mb-0">Credit Card</p>
                            </div>
                            <div>
                              {method.isDefault && (
                                <Badge bg="success" className="me-2">
                                  Default
                                </Badge>
                              )}
                              <Button variant="outline-danger" size="sm">
                                Remove
                              </Button>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" &&
          (analyticsLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading analytics...</span>
              </Spinner>
              <p className="mt-2">Loading real-time analytics...</p>
            </div>
          ) : (
            <>
              <Row>
                <Col md={6}>
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">Spending Overview</h5>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={fetchAnalytics}
                          disabled={analyticsLoading}
                        >
                          {analyticsLoading ? <Spinner size="sm" /> : "🔄"}
                        </Button>
                        <Form.Select
                          style={{ width: "auto" }}
                          value={spendingFilter}
                          onChange={(e) => setSpendingFilter(e.target.value)}
                        >
                          <option value="week">This Week</option>
                          <option value="month">This Month</option>
                          <option value="year">This Year</option>
                          <option value="allTime">All Time</option>
                        </Form.Select>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <div className="text-center">
                        <h2 className="text-success">
                          ₹{getFilteredSpending().toLocaleString()}
                        </h2>
                        <p className="text-muted">
                          {spendingFilter === "week" && "This Week Spending"}
                          {spendingFilter === "month" && "This Month Spending"}
                          {spendingFilter === "year" && "This Year Spending"}
                          {spendingFilter === "allTime" &&
                            "Total Lifetime Spending"}
                        </p>
                      </div>
                      <div className="mt-3">
                        <div className="d-flex justify-content-between mb-2">
                          <span>Booking Spent</span>
                          <span className="fw-bold">
                            ₹{analytics?.totalEarnings?.toLocaleString() || "0"}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>Total Tax</span>
                          <span className="fw-bold">
                            ₹{analytics?.totalTax?.toLocaleString() || "0"}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>Bookings This Period</span>
                          <span className="fw-bold">
                            {analytics && (
                              <>
                                {spendingFilter === "week" &&
                                  analytics.thisWeekBookings}
                                {spendingFilter === "month" &&
                                  analytics.thisMonthBookings}
                                {spendingFilter === "year" &&
                                  analytics.thisYearBookings}
                                {spendingFilter === "allTime" &&
                                  analytics.totalBookings}
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <h5 className="mb-0">Engagement</h5>
                    </Card.Header>
                    <Card.Body>
                      <div className="mt-3">
                        <p>
                          <strong>Total Bookings:</strong>{" "}
                          {analytics?.totalBookings || userData.totalBookings}
                        </p>
                        <p>
                          <strong>Completion Rate:</strong>
                          {analytics
                            ? `${analytics.completionRate.toFixed(1)}%`
                            : "0%"}
                        </p>
                        <p>
                          <strong>Referral Code:</strong>{" "}
                          {userData.referralCode}
                        </p>
                        <p>
                          <strong>Referral Points:</strong>{" "}
                          {userData.referralPoints}
                        </p>
                        <p>
                          <strong>Refer Friends Count:</strong>{" "}
                          {userData.referralsCount}
                        </p>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="mt-4">
                <Col md={12}>
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">
                        Weekly Spending Trend (Last 7 Days)
                      </h5>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={fetchAnalytics}
                          disabled={analyticsLoading}
                        >
                          {analyticsLoading ? <Spinner size="sm" /> : "🔄"}
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      {/* Weekly Summary */}
                      <div className="row mb-3">
                        <div className="col-md-4">
                          <div className="text-center p-2 bg-light rounded">
                            <div className="h5 text-success mb-1">
                              ₹
                              {getWeeklySpendingData()
                                .reduce((sum, day) => sum + day.spent, 0)
                                .toLocaleString()}
                            </div>
                            <small className="text-muted">
                              Total This Week
                            </small>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="text-center p-2 bg-light rounded">
                            <div className="h5 text-primary mb-1">
                              {
                                getWeeklySpendingData().filter(
                                  (day) => day.hasSpending
                                ).length
                              }
                            </div>
                            <small className="text-muted">Active Days</small>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="text-center p-2 bg-light rounded">
                            <div className="h5 text-info mb-1">
                              {getWeeklySpendingData().reduce(
                                (sum, day) => sum + day.bookings,
                                0
                              )}
                            </div>
                            <small className="text-muted">Total Bookings</small>
                          </div>
                        </div>
                      </div>

                      <div
                        className="weekly-chart-container"
                        style={{
                          overflowX: "auto",
                          whiteSpace: "nowrap",
                          paddingBottom: "10px",
                        }}
                      >
                        <div
                          className="d-flex justify-content-between"
                          style={{ minWidth: "max-content" }}
                        >
                          {getWeeklySpendingData().map((item, index) => (
                            <div
                              key={index}
                              className="text-center"
                              style={{
                                display: "inline-block",
                                minWidth: "80px",
                                flex: "1",
                              }}
                            >
                              <div className="fw-bold text-success">
                                ₹{item.spent.toLocaleString()}
                              </div>
                              <div className="mt-2 small text-muted">
                                {item.day}
                              </div>
                              <div
                                className="mx-auto mt-2"
                                style={{
                                  height: `${Math.max(
                                    10,
                                    item.spent > 0
                                      ? Math.max(20, item.spent / 50)
                                      : 10
                                  )}px`,
                                  width: "50px",
                                  borderRadius: "4px 4px 0 0",
                                  backgroundColor: item.hasSpending
                                    ? "#dc3545"
                                    : "#e9ecef",
                                  border: item.hasSpending
                                    ? "2px solid #dc3545"
                                    : "1px solid #dee2e6",
                                }}
                              ></div>
                              {item.hasSpending && (
                                <div className="mt-1 small text-danger fw-bold">
                                  {item.bookings} booking
                                  {item.bookings !== 1 ? "s" : ""}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ))}
      </div>

      {/* Booking Details Modal */}
      <Modal show={showBookingModal} onHide={closeBookingModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Booking Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBooking ? (
            <div>
              <Row className="mb-3">
                <Col>
                  <h6 className="mb-1">General</h6>
                  <p className="mb-1">
                    <strong>ID:</strong> {selectedBooking.id}
                  </p>
                  <p className="mb-1">
                    <strong>Status:</strong>{" "}
                    {getStatusBadge(selectedBooking.status)}
                  </p>
                  <p className="mb-1">
                    <strong>Date & Time:</strong>{" "}
                    {new Date(selectedBooking.dateTime).toLocaleString()}
                  </p>
                  <p className="mb-0">
                    <strong>Amount:</strong> ₹{selectedBooking.price}
                  </p>
                </Col>
              </Row>
              <Row>
                <Col md={6} className="mb-3">
                  <h6 className="mb-1">Vendor</h6>
                  <p className="mb-1">
                    <strong>Name:</strong> {selectedBooking.vendorName}
                  </p>
                </Col>
                <Col md={6} className="mb-3">
                  <h6 className="mb-1">Service</h6>
                  <p className="mb-1">
                    <strong>Name:</strong> {selectedBooking.serviceName}
                  </p>
                </Col>
              </Row>
            </div>
          ) : (
            <div className="text-center text-muted">No booking selected</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeBookingModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserInfo;
